<template>
  <div class="map-container h-full w-full">
    <div id="map" class="h-full w-full"></div>
    
    <!-- Map Tools -->
    <MapTools 
      v-if="map"
      :map="map"
      :property-count="propertyCount"
      :user-upload-count="userUploadCount"
      :selected-properties="selectedProperties"
      @toggle-layer="handleToggleLayer"
      @change-basemap="handleChangeBasemap"
      @reset-view="resetView"
      @toggle-selection="handleToggleSelection"
      @style-applied="handleStyleApplied"
      @style-created="handleStyleCreated"
      @style-updated="handleStyleUpdated"
      @change-heatmap-intensity="handleHeatmapIntensity"
    />
    
    <!-- Basic Map Controls -->
    <div class="absolute top-4 left-4 bg-white rounded-lg shadow-md p-2 z-10">
      <button 
        @click="zoomIn" 
        class="p-2 hover:bg-gray-100 rounded-lg"
        title="Zoom in"
      >
        <i class="fas fa-plus"></i>
      </button>
      <button 
        @click="zoomOut" 
        class="p-2 hover:bg-gray-100 rounded-lg"
        title="Zoom out"
      >
        <i class="fas fa-minus"></i>
      </button>
    </div>
    
    <!-- Property Info Popup -->
    <div v-if="selectedProperty" class="absolute bottom-4 left-4 bg-white rounded-lg shadow-md p-4 z-10 max-w-md">
      <div class="flex justify-between items-start">
        <h3 class="text-lg font-bold">{{ selectedProperty.title }}</h3>
        <button @click="selectedProperty = null" class="text-gray-500 hover:text-gray-700">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="mt-2">
        <p class="text-sm text-gray-600">{{ selectedProperty.address }}</p>
        <div class="flex justify-between mt-2">
          <span class="font-medium">${{ selectedProperty.price.toLocaleString() }}</span>
          <span 
            :class="{
              'bg-green-100 text-green-800': selectedProperty.payment_status === 'paid',
              'bg-red-100 text-red-800': selectedProperty.payment_status === 'unpaid'
            }"
            class="px-2 py-1 rounded-full text-xs"
          >
            {{ selectedProperty.payment_status }}
          </span>
        </div>
        <div class="flex space-x-4 mt-3">
          <div>
            <span class="text-gray-500 text-sm">Size</span>
            <p>{{ selectedProperty.size }} sqft</p>
          </div>
          <div>
            <span class="text-gray-500 text-sm">Bedrooms</span>
            <p>{{ selectedProperty.bedrooms }}</p>
          </div>
          <div>
            <span class="text-gray-500 text-sm">Bathrooms</span>
            <p>{{ selectedProperty.bathrooms }}</p>
          </div>
        </div>
        <div class="mt-3">
          <span class="text-gray-500 text-sm">Owner</span>
          <p>{{ selectedProperty.owner_name }}</p>
        </div>
        <div class="mt-4 flex space-x-2">
          <button 
            @click="viewPropertyDetails(selectedProperty.id)" 
            class="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-primary-700"
          >
            View Details
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, computed } from 'vue';
import { usePropertiesStore } from '../stores/properties';
import MapTools from './MapTools.vue';
import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import OSM from 'ol/source/OSM';
import XYZ from 'ol/source/XYZ';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { fromLonLat } from 'ol/proj';
import { Style, Icon, Text, Fill, Stroke, Circle } from 'ol/style';
import { defaults as defaultInteractions, Select } from 'ol/interaction';
import Heatmap from 'ol/layer/Heatmap';

const propertiesStore = usePropertiesStore();
const map = ref(null);
const vectorSource = ref(null);
const vectorLayer = ref(null);
const selectedProperty = ref(null);
const mapElement = ref(null);

// Additional layers
const heatmapLayer = ref(null);
const baseLayer = ref(null);
const satelliteLayer = ref(null);
const userUploadLayer = ref(null);

// Selection
const selectedProperties = ref([]);
const selectInteraction = ref(null);
const isSelectionModeActive = ref(false);

// Statistics
const propertyCount = computed(() => propertiesStore.properties.length);
const userUploadCount = ref(0); // Would be populated by a data import API call

// Initialize map
onMounted(async () => {
  // Initialize vector source and layer
  vectorSource.value = new VectorSource();
  vectorLayer.value = new VectorLayer({
    source: vectorSource.value,
    style: function(feature) {
      const property = feature.get('property');
      
      return new Style({
        image: new Icon({
          anchor: [0.5, 1],
          src: property.payment_status === 'paid' 
            ? 'https://cdn.jsdelivr.net/npm/feather-icons/dist/icons/check-circle.svg'
            : 'https://cdn.jsdelivr.net/npm/feather-icons/dist/icons/alert-circle.svg',
          scale: 1.5,
          color: property.payment_status === 'paid' ? '#10B981' : '#EF4444'
        }),
        text: new Text({
          text: `$${property.price.toLocaleString()}`,
          offsetY: -20,
          fill: new Fill({
            color: '#1F2937'
          }),
          stroke: new Stroke({
            color: '#FFFFFF',
            width: 3
          }),
          font: '12px sans-serif'
        })
      });
    }
  });
  
  // Create satellite layer as default
  satelliteLayer.value = new TileLayer({
    source: new XYZ({
      url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      maxZoom: 19
    }),
    visible: true
  });
  
  // Create OSM base layer (but hide it by default)
  baseLayer.value = new TileLayer({
    source: new OSM(),
    visible: false
  });
  
  // Initialize heatmap layer
  heatmapLayer.value = new Heatmap({
    source: vectorSource.value,
    blur: 15,
    radius: 10,
    visible: false,
    weight: function(feature) {
      const property = feature.get('property');
      return property ? property.price / 1000000 : 0.5; // Weight by price
    }
  });
  
  // Initialize user upload layer (empty for now)
  const userUploadSource = new VectorSource();
  userUploadLayer.value = new VectorLayer({
    source: userUploadSource,
    visible: true
  });
  
  // Create map
  map.value = new Map({
    target: 'map',
    layers: [
      satelliteLayer.value,
      baseLayer.value,
      vectorLayer.value,
      heatmapLayer.value,
      userUploadLayer.value
    ],
    view: new View({
      center: fromLonLat([36.8219, -1.2921]), // Nairobi, Kenya as default
      zoom: 12
    }),
    interactions: defaultInteractions({
      doubleClickZoom: false
    })
  });
  
  // Load properties and add to map
  await propertiesStore.fetchProperties();
  addPropertiesToMap();
  
  // Add click handler for features
  map.value.on('click', (evt) => {
    const feature = map.value.forEachFeatureAtPixel(evt.pixel, feature => feature);
    
    if (feature) {
      const property = feature.get('property');
      selectedProperty.value = property;
    } else {
      selectedProperty.value = null;
    }
  });
});

onUnmounted(() => {
  if (map.value) {
    map.value.setTarget(null);
    map.value = null;
  }
});

// Watch for changes to the properties store
watch(() => propertiesStore.properties, () => {
  addPropertiesToMap();
});

// Add properties to map
const addPropertiesToMap = () => {
  if (!vectorSource.value) return;
  
  // Clear existing features
  vectorSource.value.clear();
  
  // Add new features
  propertiesStore.properties.forEach(property => {
    // Create feature from property coordinates
    const feature = new Feature({
      geometry: new Point(fromLonLat([property.longitude, property.latitude]))
    });
    
    // Store property data with feature
    feature.set('property', property);
    
    // Add to source
    vectorSource.value.addFeature(feature);
  });
};

// Map controls
const zoomIn = () => {
  const view = map.value.getView();
  const zoom = view.getZoom();
  view.setZoom(zoom + 1);
};

const zoomOut = () => {
  const view = map.value.getView();
  const zoom = view.getZoom();
  view.setZoom(zoom - 1);
};

const resetView = () => {
  const view = map.value.getView();
  view.setCenter(fromLonLat([36.8219, -1.2921])); // Reset to Nairobi, Kenya
  view.setZoom(12);
};

// View property details
const viewPropertyDetails = (id) => {
  // This would typically navigate to a property details page
  console.log(`View property details for ID: ${id}`);
};

// Handle MapTools events
const handleToggleLayer = ({ layer, visible }) => {
  console.log(`Toggle layer ${layer}: ${visible}`);
  
  if (layer === 'properties' && vectorLayer.value) {
    vectorLayer.value.setVisible(visible);
  } else if (layer === 'heatmap' && heatmapLayer.value) {
    heatmapLayer.value.setVisible(visible);
  } else if (layer === 'userUploads' && userUploadLayer.value) {
    userUploadLayer.value.setVisible(visible);
  } else if (layer === 'baseMap' && (baseLayer.value || satelliteLayer.value)) {
    if (baseLayer.value) baseLayer.value.setVisible(visible);
    if (satelliteLayer.value) satelliteLayer.value.setVisible(visible);
  }
};

const handleChangeBasemap = (baseMapType) => {
  const view = map.value.getView();
  const layers = map.value.getLayers();
  
  // Remove old base layer
  layers.forEach((layer, i) => {
    if (layer === baseLayer.value || layer === satelliteLayer.value) {
      layers.removeAt(i);
    }
  });
  
  let newBaseLayer;
  
  switch(baseMapType) {
    case 'satellite':
      // ESRI World Imagery
      newBaseLayer = new TileLayer({
        source: new XYZ({
          url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
          maxZoom: 19
        })
      });
      break;
      
    case 'osm':
      // OpenStreetMap Standard
      newBaseLayer = new TileLayer({
        source: new OSM()
      });
      break;
      
    case 'topo':
      // ESRI Topographic
      newBaseLayer = new TileLayer({
        source: new XYZ({
          url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}',
          maxZoom: 19
        })
      });
      break;
      
    case 'street':
      // ESRI World Street Map
      newBaseLayer = new TileLayer({
        source: new XYZ({
          url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
          maxZoom: 19
        })
      });
      break;
      
    case 'terrain':
      // Stamen Terrain
      newBaseLayer = new TileLayer({
        source: new XYZ({
          url: 'https://stamen-tiles.a.ssl.fastly.net/terrain/{z}/{x}/{y}.jpg',
          maxZoom: 18
        })
      });
      break;
      
    case 'dark':
      // CartoDB Dark Matter
      newBaseLayer = new TileLayer({
        source: new XYZ({
          url: 'https://cartodb-basemaps-a.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png',
          maxZoom: 19
        })
      });
      break;
      
    case 'light':
      // CartoDB Positron (Light)
      newBaseLayer = new TileLayer({
        source: new XYZ({
          url: 'https://cartodb-basemaps-a.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png',
          maxZoom: 19
        })
      });
      break;
      
    default:
      // Default to satellite
      newBaseLayer = new TileLayer({
        source: new XYZ({
          url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
          maxZoom: 19
        })
      });
  }
  
  // Store the new layer in the appropriate ref
  if (baseMapType === 'osm') {
    baseLayer.value = newBaseLayer;
  } else {
    satelliteLayer.value = newBaseLayer;
  }
  
  // Add the new layer to the map
  layers.insertAt(0, newBaseLayer);
};

const handleToggleSelection = (active) => {
  if (active && !selectInteraction.value) {
    selectInteraction.value = new Select({
      style: new Style({
        stroke: new Stroke({
          color: '#4299e1',
          width: 3
        }),
        fill: new Fill({
          color: 'rgba(66, 153, 225, 0.2)'
        }),
        image: new Circle({
          radius: 8,
          fill: new Fill({
            color: '#4299e1'
          })
        })
      })
    });
    
    selectInteraction.value.on('select', (e) => {
      selectedProperties.value = e.selected.map(feature => feature.get('property'));
    });
    
    map.value.addInteraction(selectInteraction.value);
  } else if (!active && selectInteraction.value) {
    map.value.removeInteraction(selectInteraction.value);
    selectInteraction.value = null;
    selectedProperties.value = [];
  }
  
  isSelectionModeActive.value = active;
};

const handleStyleApplied = (data) => {
  console.log('Style applied to properties:', data);
  // Apply style to selected properties in backend
  // Then refresh the map
};

const handleStyleCreated = (style) => {
  console.log('Style created:', style);
  // Send style to backend API
  // Then refresh styles
};

const handleStyleUpdated = (style) => {
  console.log('Style updated:', style);
  // Update style in backend API
  // Then refresh styles
};

const handleHeatmapIntensity = (intensity) => {
  if (heatmapLayer.value) {
    heatmapLayer.value.setBlur(15 * intensity);
    heatmapLayer.value.setRadius(10 * intensity);
  }
};
</script>

<style scoped>
.map-container {
  position: relative;
}
</style>
